import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatearFechaHora } from '@cmms/shared';
import { api } from '../servicios/api.js';
import { Cargando, Indicador } from '../componentes/piezas.js';
import { DialogoTabletsSede } from '../componentes/tablets-sede.js';
import { useEsMovil } from '../servicios/responsive.js';

interface CuentaSedeDispositivos {
  usuarioId: string;
  nombreCompleto: string;
  sede: string;
  dispositivos: number;
  ultimoUso: string | null;
}

/**
 * Cuentas de sede con sus tablets. Solo para administración: el detalle de cada cuenta muestra
 * sus tablets, su historial y el QR de emparejamiento. No se desvincula desde esta vista.
 */
export function ListaDispositivos(): JSX.Element {
  const cliente = useQueryClient();
  const esMovil = useEsMovil();
  const [abierta, setAbierta] = useState<CuentaSedeDispositivos | null>(null);

  const { data: cuentas, isLoading } = useQuery({
    queryKey: ['dispositivos-cuentas'],
    queryFn: () => api.get<CuentaSedeDispositivos[]>('/dispositivos'),
  });

  const lista = cuentas ?? [];
  const totalTablets = lista.reduce((suma, c) => suma + c.dispositivos, 0);
  const sedesConTablet = lista.filter((c) => c.dispositivos > 0).length;
  const sedesSinTablet = lista.length - sedesConTablet;
  const ultimoUso = lista
    .map((c) => c.ultimoUso)
    .filter((v): v is string => v !== null)
    .sort()
    .at(-1);

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Dispositivos</h1>
          <p className="campo-nota">
            Tablets de cada cuenta de sede. Entre en una cuenta para ver sus tablets, su historial y generar el QR de
            emparejamiento.
          </p>
        </div>
      </div>

      <div className="rejilla-indicadores">
        <Indicador titulo="TABLETS ACTIVAS" valor={totalTablets} tono="azul" />
        <Indicador titulo="SEDES CON TABLET" valor={sedesConTablet} tono="verde" nota={`de ${lista.length} sedes`} />
        <Indicador titulo="SEDES SIN TABLET" valor={sedesSinTablet} tono="ambar" />
        <Indicador
          titulo="ÚLTIMO USO"
          valor={ultimoUso ? formatearFechaHora(ultimoUso) : '—'}
          tono="gris"
        />
      </div>

      <section className="panel">
        <div className="panel-cabecera">
          <h2>Cuentas de sede</h2>
          <span className="nav-contador">{lista.length}</span>
        </div>
        {isLoading ? (
          <Cargando texto="Cargando dispositivos…" />
        ) : lista.length === 0 ? (
          <div className="panel-cuerpo">
            <p className="campo-nota">No hay cuentas de sede con una ubicación asignada.</p>
          </div>
        ) : esMovil ? (
          <div className="tarjetas-ot">
            {lista.map((c) => (
              <div key={c.usuarioId} className="tarjeta-ot">
                <div className="tarjeta-ot-cuerpo">
                  <div className="tarjeta-ot-cabecera">
                    <div className="tarjeta-ot-titulo">{c.sede}</div>
                  </div>
                  <div className="tarjeta-ot-datos">
                    <span>{c.nombreCompleto}</span>
                    <span>{c.dispositivos} activa{c.dispositivos === 1 ? '' : 's'}</span>
                    <span>{c.ultimoUso ? formatearFechaHora(c.ultimoUso) : 'Sin uso'}</span>
                  </div>
                  <button type="button" className="boton secundario pequeno" onClick={() => setAbierta(c)}>
                    Ver detalle
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table className="tabla">
            <thead>
              <tr>
                <th>Sede</th>
                <th>Cuenta</th>
                <th>Tablets activas</th>
                <th>Último uso</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lista.map((c) => (
                <tr key={c.usuarioId}>
                  <td>{c.sede}</td>
                  <td>{c.nombreCompleto}</td>
                  <td>{c.dispositivos}</td>
                  <td>{c.ultimoUso ? formatearFechaHora(c.ultimoUso) : '—'}</td>
                  <td>
                    <div className="acciones-fila">
                      <button type="button" className="boton secundario pequeno" onClick={() => setAbierta(c)}>
                        Ver detalle
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {abierta ? (
        <DialogoTabletsSede
          usuario={{ id: abierta.usuarioId, nombreCompleto: abierta.nombreCompleto }}
          alCerrar={() => {
            setAbierta(null);
            void cliente.invalidateQueries({ queryKey: ['dispositivos-cuentas'] });
          }}
        />
      ) : null}
    </>
  );
}
